/**
 * UsersSection.tsx — the Users pane inside Settings (moved 2026-08-10 from
 * its own `/users` route — Andrew: it is staff configuration, so it belongs
 * with the rest of configuration rather than off on its own). Same list,
 * same per-row controls, same disabled-with-reason treatment as the old
 * `UsersPage.tsx` (users-and-permissions design doc,
 * docs/superpowers/specs/2026-08-09-users-and-permissions-design.md §6) —
 * only where it lives changed. The router-level `/users` route, its
 * `RequireAdmin` gate and the standalone page's own "Back to the console"
 * chrome are gone; `SettingsPage.tsx` already owns a back button and a
 * per-section heading, so this component is just the pane's body now.
 *
 * NOT CONFIG. Every other settings section is generated from
 * `CONFIG_FIELDS` (sections.ts's own header comment) and renders as
 * `FieldRow`s; this one is a live directory of people with per-row actions,
 * which is not a shape `ConfigField` can honestly express. `sections.ts`
 * marks this section `body: 'custom'` instead of inventing field entries to
 * force it through the generic renderer — see that file's own comment.
 *
 * SELF-GATES ON ADMIN, IN PLACE OF THE OLD ROUTE GATE. Every other settings
 * section is visible to everyone and disables what a non-admin may not
 * touch (`SettingsPage`'s own "Not admin · tenant settings read-only"
 * chip) — but `GET /ops/users` refuses a non-admin outright (`canListUsers`,
 * @communicator/shared), so there is no list this pane could show one.
 * Hiding the section entirely would make Users the one settings pane that
 * silently isn't there for some viewers — the same "a hidden control
 * teaches nothing" instinct design §6 applies to individual row controls,
 * applied here to the section as a whole. So the pane always appears in the
 * nav, and for a non-admin explains plainly why there is nothing to list,
 * before ever attempting `ops.users()` — never the request a non-admin's
 * own role already guarantees a 403 for.
 *
 * A consequence: every `actor` `userPermissions.ts`'s per-row checks ever
 * see below still has `admin: true` (see that file's own note) — the
 * question "can this viewer see the list at all" is decided by the early
 * return just below, before any row-level permission check runs, same as
 * it was decided by `RequireAdmin` before this component ever mounted.
 */
import { Fragment, useEffect, useRef, useState } from 'react'
import type { StaffRole } from '@communicator/shared'
import { ops } from '../../lib/ops'
import type { HaloAgent, StaffUser } from '../../lib/ops'
import { useStore } from '../../store'
import { Avatar, Card, Chip, Icon, Select, Toggle } from '../ui'
import {
  canChangeActive,
  canChangeAdmin,
  canChangeExtension,
  canChangeHaloAgentName,
  canChangeRole,
} from '../../lib/userPermissions'
import type { UiPermissionResult } from '../../lib/userPermissions'
import { initialsOf } from '../thread/format'
import { UserSessions } from './UserSessions'

/**
 * README §7b's `grid-template-columns:1fr 150px 190px 200px`, shared by the
 * `--surface-sunken` header row below and every data row, so the columns line
 * up exactly.
 *
 * RETUNED when Extension became a fifth column, because the original widths
 * no longer fit and every cell collapsed on top of its neighbour. The maths,
 * which is the whole bug: the settings pane caps a section at 720px and the
 * card spends 32px on padding, leaving ~688px — while five fixed columns at
 * the old widths plus four 12px gaps demanded 698px BEFORE the Person column
 * was given a single pixel. Four columns had already been tight; the fifth
 * simply exceeded the box.
 *
 * Three things keep it honest now:
 *   - the Users section is allowed more width than a form section (see
 *     SettingsPage's `max-w`), because 720px is a READING width for field
 *     rows and this is a data table;
 *   - the control columns are narrower, and Extension is the narrowest of
 *     all — it holds three or four digits, never a word;
 *   - Person is `minmax(200px,1fr)`, so it can never be squeezed to nothing,
 *     and the card scrolls sideways if the window is genuinely too narrow.
 *     Overflowing into a scroll is recoverable; overlapping text is not.
 *
 * The horizontal layout now starts at `lg` rather than `md`: between the two
 * there is not enough room for five controls, and the stacked single-column
 * form each row falls back to is perfectly readable.
 */
export const USERS_ROW_GRID =
  // Seven columns now that Devices (Task 3, signed-in devices) has joined:
  // Person, Extension, Halo agent, Role, Admin, Active, Devices.
  //
  // THE ARITHMETIC, updated rather than eyeballed, because this table has
  // collapsed once already and the test below enforces it. The section is
  // max-w-[1040px] (SettingsPage's `sectionMaxWidth` — tables get 1040,
  // reading-width sections get 720), less the Card's px-4 both sides = 1008
  // usable. Seven tracks means SIX gap-3 gaps now, not five — 72px, not 60 —
  // which itself eats 12px of the six-column version's "44px of headroom for
  // a seventh column" comment. Person's floor is 200 and is NOT negotiable —
  // it holds an avatar, a name, an email and up to two chips. That leaves
  // 736 for the six fixed columns; the original five (Extension/Halo
  // agent/Role/Admin/Active) still use exactly 704, leaving 32px for
  // Devices — which is all it needs, since it holds one chevron button, not
  // a word.
  //
  // The first attempt at the original five-column version shrank Person to
  // 180 to make room. CI caught it. Squeezing the one column that carries
  // prose is exactly what made cells overlap when Extension was added, so
  // Devices deliberately does not repeat that mistake — it fits in the
  // margin instead, and the margin shrank to 32px (not 44) once the extra
  // gap this column itself introduces was accounted for.
  //
  // Halo agent is the widest fixed column because it holds a person's full
  // display name ("Elisabeth Falkenstein"), not a 3-digit extension, and it
  // has to be readable while being typed — a truncated name is unverifiable,
  // and Halo matches it character for character.
  'grid grid-cols-1 gap-3 px-4 py-3 lg:grid-cols-[minmax(200px,1fr)_84px_200px_130px_140px_150px_32px] lg:items-start'

/**
 * The Halo agent cell.
 *
 * A PICKER, NOT A TEXT BOX, and that is a correctness decision rather than a
 * convenience one. `who` matches a Halo agent's name character for character,
 * and Halo's `name` is free-form: on this instance Jason is "Jason", Timmy is
 * "Timmy", and Kat and Mark are their EMAIL ADDRESSES — four of seven real
 * agents would be typed wrong by anyone reasoning from the person's actual
 * name. Halo does not reject a name it cannot find; it resolves it to
 * `who_agentid = -1` and the time silently lands on nobody. A dropdown makes
 * that class of mistake impossible.
 *
 * Two cases it must not confuse:
 *   - Halo UNREACHABLE (`available: false`) — fall back to a text box, because
 *     a picker with nothing in it reads as "you have no agents", and an admin
 *     must not be blocked from working because a record system is down.
 *   - A stored name that is no longer in the list (renamed or deactivated in
 *     Halo). It stays selectable and is marked, rather than silently vanishing
 *     from the row and leaving the field looking unset while the database still
 *     holds it.
 */
function HaloAgentCell({
  value,
  halo,
  disabled,
  onCommit,
  testId,
  ariaLabel,
}: {
  value: string | null
  halo: { agents: HaloAgent[]; available: boolean } | null
  disabled: boolean
  onCommit: (value: string | null) => void
  testId: string
  ariaLabel: string
}) {
  // Not answered yet: no control at all would make the row jump when it lands.
  if (halo === null) {
    return (
      <Select
        value=""
        disabled
        className="w-full"
        aria-label={ariaLabel}
        data-testid={testId}
        options={[{ value: '', label: 'Loading…' }]}
        onChange={() => {}}
      />
    )
  }

  if (!halo.available) {
    return (
      <DeferredTextInput
        value={value}
        disabled={disabled}
        onCommit={onCommit}
        testId={testId}
        ariaLabel={ariaLabel}
      />
    )
  }

  const stored = value ?? ''
  const known = halo.agents.some((a) => a.name === stored)
  const options = [
    // Clearing must stay possible: somebody who leaves has to stop being
    // attributed.
    { value: '', label: '— not attributed —' },
    // A stored name Halo no longer returns (renamed, or deactivated). Kept
    // selectable and marked, rather than dropping out of the list and leaving
    // the row looking unset while the database still holds the value.
    ...(stored !== '' && !known ? [{ value: stored, label: `${stored} (not in Halo)` }] : []),
    ...halo.agents.map((a) => ({ value: a.name, label: a.name })),
  ]
  return (
    <Select
      value={stored}
      disabled={disabled}
      className="w-full"
      aria-label={ariaLabel}
      data-testid={testId}
      options={options}
      onChange={(v) => onCommit(v === '' ? null : v)}
    />
  )
}

/**
 * A free-text cell in a user row — the extension, and the Halo agent name.
 *
 * DEFERRED COMMIT, and it is the whole reason this is a component rather than
 * an inline `<input>`. Every other control in this row is a Select or a Toggle
 * — one interaction, one value, commit immediately. A text field is not:
 * committing per keystroke would PATCH "6", then "60", then "605", leaving
 * whichever response landed last as the stored answer, and would put three
 * writes in the audit trail for one edit. So it commits on blur and on Enter.
 *
 * Local state seeded from the row, and re-seeded when the row's value changes
 * underneath it (another admin editing the same person, or the read-back after
 * a save landing a trimmed value) — but never while it is focused, which would
 * yank characters out from under someone mid-type.
 *
 * Generalised from the extension-only version when the Halo agent name arrived:
 * both are admin-set identifiers from another system, matched exactly, with the
 * same "cleared means null" rule. One implementation, so a fix to the commit
 * behaviour cannot land on one field and miss the other.
 */
function DeferredTextInput({
  value: storedValue,
  disabled,
  onCommit,
  testId,
  ariaLabel,
  placeholder = '—',
  inputMode,
}: {
  value: string | null
  disabled: boolean
  onCommit: (value: string | null) => void
  testId: string
  ariaLabel: string
  placeholder?: string
  inputMode?: 'numeric' | 'text'
}) {
  const stored = storedValue ?? ''
  const [value, setValue] = useState(stored)
  const focused = useRef(false)

  useEffect(() => {
    if (!focused.current) setValue(stored)
  }, [stored])

  function commit() {
    const trimmed = value.trim()
    // Nothing changed — including "605" edited to " 605 " — so nothing is
    // written. A no-op PATCH would still land in the audit trail as an edit.
    if (trimmed === stored) {
      setValue(stored)
      return
    }
    // Cleared means cleared: null, not "". Someone who moves desks — or leaves
    // — has to be removable, or the system goes on attributing to them.
    onCommit(trimmed === '' ? null : trimmed)
  }

  return (
    <input
      type="text"
      inputMode={inputMode}
      data-testid={testId}
      aria-label={ariaLabel}
      className="w-full rounded-control border border-default bg-sunken px-2 py-[5px] text-[13px] text-hi disabled:opacity-60"
      placeholder="—"
      value={value}
      disabled={disabled}
      onChange={(e) => setValue(e.target.value)}
      onFocus={() => {
        focused.current = true
      }}
      onBlur={() => {
        focused.current = false
        commit()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          e.currentTarget.blur()
        }
      }}
    />
  )
}

const ROLE_OPTIONS = [
  { value: 'dispatch', label: 'Dispatch' },
  { value: 'tech', label: 'Tech' },
]

/** Refused-by-default, with an honest reason, for the brief window (if any) before `store.me` has a value. */
const IDENTITY_NOT_READY: UiPermissionResult = { ok: false, reason: 'Your identity has not loaded yet.' }

export function UsersSection() {
  const me = useStore((s) => s.me)
  const admin = useStore((s) => s.admin)
  const superAdmin = useStore((s) => s.superAdmin)
  const pushToast = useStore((s) => s.pushToast)

  const [users, setUsers] = useState<StaffUser[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // null = not answered yet. `available: false` means Halo was unreachable,
  // which is NOT the same as "no agents" — see the fallback in the cell below.
  const [halo, setHalo] = useState<{ agents: HaloAgent[]; available: boolean } | null>(null)
  // Which rows' Devices expansion (Task 3, signed-in devices) is open. A
  // plain id set rather than a single "which row" value — nothing here
  // limits an admin to inspecting one person's sessions at a time.
  const [devicesOpen, setDevicesOpen] = useState<Set<string>>(new Set())

  function toggleDevices(id: string) {
    setDevicesOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  useEffect(() => {
    // See this file's own header comment: a non-admin's `GET /ops/users`
    // is a guaranteed 403, so this never even fires the request for one —
    // the same tidiness `RequireAdmin`'s own removed comment described,
    // now enforced here instead of by a route wrapper.
    if (!admin) return
    let cancelled = false
    ops
      .users()
      .then((list) => {
        if (!cancelled) setUsers(list)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : 'Could not load the staff list.')
      })
    // The picker's list. Deliberately NOT awaited with the user list: a slow or
    // unreachable Halo must not delay the staff table, which is useful on its
    // own. A failure here is not surfaced as an error either — the cell simply
    // degrades to a text box.
    ops
      .haloAgents()
      .then((res) => {
        if (!cancelled) setHalo(res)
      })
      .catch(() => {
        if (!cancelled) setHalo({ agents: [], available: false })
      })
    return () => {
      cancelled = true
    }
  }, [admin])

  const actor = me ? { id: me.id, admin, superAdmin: superAdmin === true } : null

  function applyUpdate(updated: StaffUser) {
    setUsers((prev) => (prev ? prev.map((u) => (u.id === updated.id ? updated : u)) : prev))
  }

  async function patch(
    target: StaffUser,
    body: Partial<Pick<StaffUser, 'role' | 'admin' | 'active' | 'ext' | 'haloAgentName'>>,
  ) {
    try {
      const updated = await ops.updateUser(target.id, body)
      applyUpdate(updated)
    } catch (err) {
      // Refused or unreachable: the row is left exactly as it was (nothing
      // above optimistically applied the requested value) — the same
      // "server decides, UI reflects" discipline CoverageToggle uses for the
      // caller's own row.
      pushToast({
        text: err instanceof Error ? err.message : `Could not update ${target.name}. Try again.`,
        tone: 'bad',
      })
    }
  }

  if (!admin) {
    // Same reason `canListUsers` gives server-side (packages/shared/src/
    // permissions.ts) — this is not a separately invented explanation, it is
    // the real one, said before the request that would otherwise say it.
    return (
      <p className="text-[13px] text-mid" data-testid="users-admin-required">
        Reading the staff list requires an admin. Ask an admin to grant you admin from this pane.
      </p>
    )
  }

  return (
    <div data-testid="users-section">
      {loadError ? (
        <p className="text-[13px] text-bad" data-testid="users-load-error">
          {loadError}
        </p>
      ) : users === null ? (
        <p className="text-[13px] text-mid">Loading…</p>
      ) : users.length === 0 ? (
        <p className="text-[13px] text-mid">No staff on record.</p>
      ) : (
        <Card className="overflow-x-auto divide-y divide-subtle">
          {/* README §7b: "a --surface-sunken header row of 10.5px/600 .08em
              eyebrows." Desktop only — a phone stacks each row's own labels
              instead (the toggles/select below already carry their own
              `label`/`aria-label`), same convention FieldRow's grid uses. */}
          <div
            data-testid="users-header-row"
            className={['hidden bg-sunken text-[10.5px] font-semibold uppercase tracking-[.08em] text-lo lg:grid', USERS_ROW_GRID].join(' ')}
          >
            <span>Person</span>
            <span>Extension</span>
            <span>Halo agent</span>
            <span>Role</span>
            <span>Admin</span>
            <span>Active</span>
            {/* No text label here — the 32px column (see USERS_ROW_GRID's
                own comment) holds a single chevron button, and "Devices"
                would wrap in that width. The button's own aria-label
                carries the meaning for anyone not reading it visually. */}
            <span aria-hidden="true" />
          </div>
          {users.map((u) => {
            const target = { id: u.id, admin: u.admin, superAdmin: u.superAdmin, active: u.active }
            const roleCheck = actor ? canChangeRole(actor, target) : IDENTITY_NOT_READY
            const adminCheck = actor ? canChangeAdmin(actor, target) : IDENTITY_NOT_READY
            // A toggle click always requests the flip of the row's current
            // state — the check has to name that direction explicitly now
            // that reactivating the SuperAdmin is permitted but
            // deactivating them still is not (userPermissions.ts).
            const activeCheck = actor ? canChangeActive(actor, target, !u.active) : IDENTITY_NOT_READY
            // An extension confers no authority, so unlike role and admin it
            // is not locked on the SuperAdmin's row — see canChangeExtension.
            const extCheck = actor ? canChangeExtension(actor) : IDENTITY_NOT_READY
            // Same reasoning as the extension: admin-only, but not locked on
            // the SuperAdmin's row — see canChangeHaloAgentName.
            const haloNameCheck = actor ? canChangeHaloAgentName(actor) : IDENTITY_NOT_READY
            const isSelf = me?.id === u.id
            const devicesShown = devicesOpen.has(u.id)

            return (
              <Fragment key={u.id}>
              <div data-testid={`user-row-${u.id}`} className={USERS_ROW_GRID}>
                <div className="flex min-w-0 items-start gap-2.5">
                  <Avatar
                    initials={initialsOf(u.name)}
                    role={u.role}
                    size={32}
                    className="mt-0.5 flex-none"
                  />
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-medium text-hi">
                      {u.name}
                      {isSelf ? <span className="text-lo"> (you)</span> : ''}
                    </p>
                    <p className="text-[12px] text-mid">{u.email}</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {/* An identity marker, not a status — README §7b puts
                          SuperAdmin on --teal-50, the brand tint, not any of
                          Chip's status tones. */}
                      {u.superAdmin ? <Chip tone="brand">SuperAdmin</Chip> : null}
                      {!u.active ? <Chip tone="warn">Deactivated</Chip> : null}
                    </div>
                    {u.superAdmin ? (
                      <p className="mt-1 text-[11px] text-lo" data-testid={`user-row-${u.id}-protected`}>
                        Protected: the SuperAdmin cannot be deactivated, and only the SuperAdmin may change their
                        own role or admin flag.
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  {/* Committed on blur and on Enter, never per keystroke: a
                      PATCH for every character would write "6", "60", "605"
                      and leave whichever landed last as the answer. */}
                  <DeferredTextInput
                    value={u.ext}
                    disabled={!extCheck.ok}
                    onCommit={(value) => void patch(u, { ext: value })}
                    testId={`user-row-${u.id}-ext`}
                    ariaLabel={`Phone extension for ${u.name}`}
                    inputMode="numeric"
                  />
                  {!extCheck.ok ? (
                    <p className="flex items-center gap-1 text-[11px] text-lo">
                      <Icon name="lock" size={11} />
                      {extCheck.reason}
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-col gap-1">
                  {/* Picked, never typed — see HaloAgentCell for why. */}
                  <HaloAgentCell
                    value={u.haloAgentName}
                    halo={halo}
                    disabled={!haloNameCheck.ok}
                    onCommit={(value) => void patch(u, { haloAgentName: value })}
                    testId={`user-row-${u.id}-halo-agent-name`}
                    ariaLabel={`Halo agent name for ${u.name}`}
                  />
                  {haloNameCheck.ok ? (
                    halo && !halo.available ? (
                      <p className="text-[11px] text-lo">Halo unreachable — type the exact name</p>
                    ) : null
                  ) : (
                    <p className="flex items-center gap-1 text-[11px] text-lo">
                      <Icon name="lock" size={11} />
                      {haloNameCheck.reason}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <Select
                    value={u.role}
                    disabled={!roleCheck.ok}
                    // Fills its column, like the extension box beside it —
                    // an intrinsically-sized control in a fixed grid column
                    // leaves a ragged edge next to a full-width neighbour.
                    className="w-full"

                    aria-label={`Role for ${u.name}`}
                    options={ROLE_OPTIONS}
                    onChange={(value) => void patch(u, { role: value as StaffRole })}
                  />
                  {/* README §7b: "when refused it goes --surface-sunken with
                      a lock icon in place of the chevron." The native
                      <select>'s own OS-drawn chevron can't be swapped for an
                      icon without replacing the control itself (and
                      overriding its background via a second Tailwind
                      utility class is exactly the cascade-ordering hazard
                      FieldRow.tsx's own comment warns against — the two
                      classes' relative CSS order isn't guaranteed by the
                      className string). The lock beside the disabled
                      control's own reason is the same "refused, and here is
                      why" signal, just not a pixel-exact recreation. */}
                  {!roleCheck.ok ? (
                    <p className="flex items-center gap-1 text-[11px] text-lo">
                      <Icon name="lock" size={11} />
                      {roleCheck.reason}
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-col gap-1">
                  <Toggle
                    checked={u.admin}
                    disabled={!adminCheck.ok}
                    aria-label={`Admin for ${u.name}`}
                    label={u.admin ? 'Admin' : 'Not admin'}
                    onChange={(checked) => void patch(u, { admin: checked })}
                  />
                  {!adminCheck.ok ? <p className="text-[11px] text-lo">{adminCheck.reason}</p> : null}
                </div>

                <div className="flex flex-col gap-1">
                  <Toggle
                    checked={u.active}
                    disabled={!activeCheck.ok}
                    aria-label={`Active for ${u.name}`}
                    label={u.active ? 'Active' : 'Deactivated'}
                    onChange={(checked) => void patch(u, { active: checked })}
                  />
                  {!activeCheck.ok ? <p className="text-[11px] text-lo">{activeCheck.reason}</p> : null}
                </div>

                <div className="flex items-start justify-end lg:justify-center">
                  {/* Task 3, signed-in devices. Chevron affordance, same
                      shape as CallFloor.tsx's own toggle — rotated when
                      closed rather than swapped for a different icon, so
                      the control's identity stays put and only its state
                      changes. Admin-only by construction: this whole
                      section already returned early above for anyone but
                      an admin, so every row here is already admin-gated. */}
                  <button
                    type="button"
                    data-testid={`user-row-${u.id}-devices-toggle`}
                    aria-label={`Devices for ${u.name}`}
                    aria-expanded={devicesShown}
                    onClick={() => toggleDevices(u.id)}
                    className="flex h-7 w-7 flex-none items-center justify-center rounded-control text-mid hover:bg-card"
                  >
                    <span
                      style={{ transform: devicesShown ? undefined : 'rotate(-90deg)', display: 'inline-flex' }}
                    >
                      <Icon name="chevron-down" size={15} />
                    </span>
                  </button>
                </div>
              </div>
              {devicesShown ? <UserSessions userId={u.id} userName={u.name} /> : null}
              </Fragment>
            )
          })}
        </Card>
      )}
    </div>
  )
}
